import type { EthOrSolanaAssetIdFormat } from "@repo/types";
import { isValidEthOrSolanaAddress } from "@repo/utils";
import { type Hex, isAddress } from "viem";
import z from "zod/v4";

// Spot check to see if the object is duck-typed.
// FOOTGUN(johnrjj) - Does NOT check children for types, so the tree itself is not fully typechecked.
// Only the top level object is checked. It is your responsibility to check the children - zod is unable to due to generics.
export const StrategyBaseSchema_StrategyChildrenNotTypeChecked = z
  .object({
    entry: z
      .object({
        blockIdForInstance: z.string(),
        blockType: z.string(),
        children: z.array(z.any()).or(z.any()).optional(),
        name: z.string(),
        parentNodeId: z.string(),
      })
      .passthrough(), // TODO think about ways to support typing this as best as possible.
    tradingSettings: z.union([
      z.object({
        triggerPercentage: z.number(),
        type: z.literal("threshold"),
      }),
      z.object({
        frequency: z.string(),
        type: z.literal("calendar"),
      }),
    ]),
    version: z.string().optional(),
  })
  // Top-level passthrough is LOAD-BEARING: additive document keys (e.g. the
  // direct index builder's `directIndex` provenance) must survive the tRPC
  // save boundary. Zod's default object behavior silently strips them.
  .passthrough();

type AssetIdFormat = `${Hex}:${string}`;

export const AssetIdFormatZodType = z.custom<EthOrSolanaAssetIdFormat>(
  (value): value is EthOrSolanaAssetIdFormat => {
    if (typeof value !== "string") {
      return false;
    }
    try {
      const [address, chainId] = value.split(":");
      if (
        !address ||
        !chainId ||
        !isValidEthOrSolanaAddress(address) ||
        Number.isNaN(Number(chainId))
      ) {
        return false;
      }
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  },
  { error: "Invalid AssetIdFormat" },
);

export const AssetZodType = z.object({
  assetId: AssetIdFormatZodType,
});

export const AssetWithAmountZodType = AssetZodType.extend({
  amount: z.string(),
  // Optional UI metadata used by clients before converting to smallest units.
  decimals: z.number().int().nonnegative().optional(),
  tokenPriceInUsd: z.string().optional(),
  symbol: z.string().optional(),
  tokenIcon: z.string().optional(),
});

export type AssetWithAmount = z.infer<typeof AssetWithAmountZodType>;

export const StrategyBlueprintSchema = z.object({
  blueprint_id: z.string().nonempty(),
  blueprint_uid: z.string().nonempty(),
  edit_id: z.string().nonempty(),
});
